/**
 * Onboarding Checklist
 *
 * Lightweight "try these next" checklist for user-controlled surfaces.
 *
 * Primary responsibilities:
 * - Show activation progress without forcing prompts on every screen
 * - Follow the first-14/30 Losi-max journey order
 * - Link users to unused high-value features
 * - Dismiss for ~2 weeks (same snooze as discovery prompts)
 *
 * Dependencies:
 * - Discoverability context
 * - Feature registry + journey
 */

'use client'

import { useRouter } from 'nextjs-toploader/app'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useMemo } from 'react'

import { DiscoverabilityFeatureIcon } from '@/components/discoverability/discoverability-feature-icon'
import {
  getDiscoverabilityFeature,
  isDiscoverabilityFeatureAvailable
} from '@/lib/discoverability/features'
import { runDiscoverabilityCta } from '@/lib/discoverability/cta-actions'
import { useDiscoverability } from '@/lib/discoverability/context'
import {
  DISCOVERABILITY_JOURNEY_STRETCH_DAYS,
  DISCOVERABILITY_JOURNEY_STEPS,
  getAccountAgeDays,
  getDiscoverabilityJourneyProgress
} from '@/lib/discoverability/journey'
import { useAuth } from '@/context/auth'
import { cn } from '@/lib/utils'

export const EMPTY_CHAT_CHECKLIST_DISMISS_ID = 'empty_chat:onboarding_checklist'

export type OnboardingChecklistProps = {
  className?: string
}

export function OnboardingChecklist({ className }: OnboardingChecklistProps) {
  const router = useRouter()
  const { usage, userPlan, enabledBetaFeatures, ready, isPromptDismissed, dismissPrompt } =
    useDiscoverability()
  const { profile, user } = useAuth()

  const accountAgeDays = getAccountAgeDays(
    (profile as { created_at?: string | null } | null)?.created_at ?? user?.created_at ?? null
  )

  const journey = useMemo(() => {
    if (accountAgeDays == null) return null
    return getDiscoverabilityJourneyProgress({
      accountAgeDays,
      usage,
      userPlan,
      enabledBetaFeatures
    })
  }, [accountAgeDays, enabledBetaFeatures, usage, userPlan])

  if (!ready) return null
  if (isPromptDismissed(EMPTY_CHAT_CHECKLIST_DISMISS_ID)) return null
  if (accountAgeDays == null || !journey) return null

  if (accountAgeDays > DISCOVERABILITY_JOURNEY_STRETCH_DAYS && journey.completedCount > 0) {
    if (journey.unlockedUnusedKeys.length === 0) return null
  }

  // Only unused unlockable steps — completed rows are progress noise.
  const steps = DISCOVERABILITY_JOURNEY_STEPS.filter((step) => {
    if (
      !isDiscoverabilityFeatureAvailable(step.featureKey, {
        userPlan,
        enabledBetaFeatures
      })
    ) {
      return false
    }
    if (accountAgeDays < step.unlockDay) return false
    const row = usage[step.featureKey]
    const used = Boolean(
      row?.firstUsedAt || (row?.useCount ?? 0) > 0 || row?.completedOnboardingAt
    )
    return !used
  })

  if (steps.length === 0) return null

  // Cap density — journey next + one more (not a full product catalog).
  const visibleSteps = (() => {
    const nextKey = journey.nextFeatureKey
    const next = nextKey ? steps.find((s) => s.featureKey === nextKey) : undefined
    const rest = steps.filter((s) => s.featureKey !== nextKey)
    return [...(next ? [next] : []), ...rest].slice(0, 2)
  })()

  const left = steps.length

  return (
    <section
      className={cn('space-y-3', className)}
      aria-label={left === 1 ? 'One feature left' : `${left} features left`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-muted-foreground text-xs leading-5">
          {left === 1 ? 'One left when it helps.' : `${left} left — pick one when it helps.`}
        </p>
        <button
          type="button"
          aria-label="Dismiss suggestions"
          onClick={() => dismissPrompt(EMPTY_CHAT_CHECKLIST_DISMISS_ID)}
          className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 -mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors"
        >
          <Cross2Icon className="size-4" aria-hidden="true" />
        </button>
      </div>

      <div className="bg-background divide-y rounded-lg border">
        {visibleSteps.map((step) => {
          const feature = getDiscoverabilityFeature(step.featureKey)
          const isNext = journey.nextFeatureKey === step.featureKey

          return (
            <button
              key={step.featureKey}
              type="button"
              onClick={() => {
                const href = runDiscoverabilityCta(step.featureKey)
                router.push(href)
              }}
              className={cn(
                'hover:bg-muted/60 flex w-full items-start gap-3 p-3 text-left transition-colors',
                isNext && 'bg-muted/40'
              )}
            >
              <span className="bg-muted mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border">
                <DiscoverabilityFeatureIcon
                  featureKey={step.featureKey}
                  size={15}
                  className="text-foreground"
                />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">{feature.label}</span>
                {isNext ? (
                  <span className="text-muted-foreground block text-xs leading-5">
                    {feature.shortDescription}
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
